// src/components/generic-crud/GenericListView.js
import { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import {
  Box,
  Tab,
  Tabs,
  Card,
  Stack,
  Button,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  Alert,
} from '@mui/material';
import { useDebounce } from 'src/hooks/use-debounce';
import { useBoolean } from 'src/hooks/use-boolean';
import { useLocalStorage } from 'src/hooks/use-local-storage';
import { useAuth } from 'src/hooks/useAuth';
import { useTable, TableColumnVisibility, TablePaginationCustom } from 'src/components/table';
import { useSnackbar } from 'notistack';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import useSWR from 'swr';
import axiosInstance, { fetcher, postFetcher, endpoints } from 'src/utils/axios';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import { QuickFormDialog } from 'src/components/custom-dialog';
import { useRights } from 'src/hooks/useRights';
import GenericTable from './GenericTable';
import GenericCardView from './GenericCardView';
import GenericToolbar from './GenericToolbar';
import GenericFiltersResult from './GenericFiltersResult';
import AdvancedFiltersModal from './AdvancedFiltersModal';
import ImportDialog from './ImportDialog';
import ExportDialog from './ExportDialog';
// Generic List View - Robust Import Integration
export default function GenericListView({
  config,
  breadcrumbs,
  onBulkAction,
  bulkActionLabel,
  refreshKey,
  extraActions,
  onDuplicate,
  ImportDialogComponent, // Custom import component (e.g., Wizard)
  preTableContent, // Optional content to render above breadcrumbs
}) {
  const { entity, endpoints: configEndpoints, fetch: fetchConfig, form, table: tableConfig, filters: filterConfig, features, card } = config;
  const settings = useSettingsContext();
  const { user, refreshUser } = useAuth();

  // 🛡️ Centralized Rights Hook (Opts-in if entity.rights is provided)
  const rightsState = useRights(entity?.rights?.module, entity?.rights?.menu, entity?.rights?.submenu);

  const table = useTable({
    defaultRowsPerPage: tableConfig.defaultRowsPerPage || 10,
    storageKey: `${entity.storageKey}-rows-per-page`,
  });
  const quickCreate = useBoolean();
  const advancedFilters = useBoolean();
  const importDialog = useBoolean();
  const exportDialog = useBoolean();
  const { enqueueSnackbar } = useSnackbar();

  const [currentItem, setCurrentItem] = useState(null);
  const [prefillData, setPrefillData] = useState(null);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [filters, setFilters] = useState(filterConfig.default);
  const [reporteeType, setReporteeType] = useState('all'); // 'all' | 'my'

  // 🛡️ Centralized Admin & Rights Logic
  const processedFeatures = useMemo(() => {
    const role = user?.role;
    const isAdmin = role === 'super_admin' || role === 'admin';

    // If entity.rights is provided, dynamically override feature flags, BUT respect hardcoded 'false' in config
    const isRightsManaged = !!entity?.rights;

    let canDeleteAll = false;
    if (features.deleteAll !== false && features.delete !== false) {
      if (isRightsManaged) {
        canDeleteAll = rightsState.canDeleteAll;
      } else {
        canDeleteAll = isAdmin || features.deleteAll;
      }
    }

    let canCreate = Boolean(features.create);
    if (typeof features.create === 'boolean') {
      canCreate = features.create;
    } else if (isRightsManaged) {
      canCreate = Boolean(rightsState.canAdd || rightsState.canApply);
    }

    return {
      ...features,
      isAdmin: isAdmin || features.isAdmin || rightsState.isAdmin,

      // Dynamic rights integration (only override if explicitly managed via config.entity.rights)
      create: canCreate,
      edit: isRightsManaged && features.edit !== false ? rightsState.canEdit : features.edit,
      delete: isRightsManaged && features.delete !== false ? rightsState.canDelete : features.delete,
      duplicate: isRightsManaged && features.duplicate !== false ? rightsState.canDuplicate : features.duplicate,
      export: isRightsManaged && features.export !== false ? rightsState.canExport : features.export,
      import: isRightsManaged && features.import !== false ? rightsState.canImport : features.import,

      // Filter customActions based on rights if managed
      customActions: isRightsManaged && features.customActions
        ? features.customActions.filter(action => {
          if (action.action === 'edit' && !rightsState.canEdit) return false;
          if (action.action === 'delete' && !rightsState.canDelete) return false;
          if (action.action === 'duplicate' && !rightsState.canDuplicate) return false;
          if ((action.action === 'add' || action.action === 'create') && !rightsState.canAdd && !rightsState.canApply) return false;
          if ((action.action === 'approve' || action.action === 'reject' || action.action === 'fulfill' || action.action === 'process') && !rightsState.hasRight('approve_all') && !rightsState.hasRight('approve_self')) return false;
          return true;
        })
        : features.customActions,

      // Granular permissions
      editAll: isRightsManaged ? rightsState.canEditAll : (isAdmin || features.editAll),
      editSelf: isRightsManaged ? rightsState.canEditSelf : features.editSelf,
      deleteAll: canDeleteAll,
      deleteSelf: isRightsManaged ? rightsState.canDeleteSelf : features.deleteSelf,
      viewAll: isRightsManaged ? rightsState.canViewAll : (isAdmin || features.viewAll),
      viewSelf: isRightsManaged ? rightsState.canViewSelf : features.viewSelf,

      bulkDelete: isRightsManaged && features.bulkDelete !== false ? rightsState.canDelete : features.bulkDelete,
      bulkApprove: isRightsManaged && features.bulkApprove !== false ? (rightsState.hasRight('approve_all') || rightsState.hasRight('approve_self')) : features.bulkApprove,
      bulkReject: isRightsManaged && features.bulkReject !== false ? (rightsState.hasRight('approve_all') || rightsState.hasRight('approve_self')) : features.bulkReject,
    };
  }, [features, user?.role, entity?.rights, rightsState]);

  // Custom Import Dialog Rendering
  const renderImportDialog = () => {
    if (!processedFeatures.import || !importDialog.value) return null;

    if (ImportDialogComponent) {
      return (
        <ImportDialogComponent
          open={importDialog.value}
          onClose={importDialog.onFalse}
          formMaster={config._formMaster}
          onRefresh={mutate}
        />
      );
    }

    return (
      <ImportDialog
        open={importDialog.value}
        onClose={importDialog.onFalse}
        config={config}
        onSuccess={() => {
          mutate();
          enqueueSnackbar('Import completed successfully!', { variant: 'success' });
        }}
      />
    );
  };

  // Toggle status state - track rows being toggled
  const [togglingRows, setTogglingRows] = useState(new Set());

  // Sorting state
  const [sortField, setSortField] = useState(tableConfig.defaultSort?.field || 'createdAt');
  const [sortDirection, setSortDirection] = useState(tableConfig.defaultSort?.direction || 'desc');

  // Handle sort change
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  // Notify parent of filter changes
  useEffect(() => {
    if (config.onFiltersChange) {
      config.onFiltersChange(filters);
    }
  }, [filters, config]);

  // Debounced search
  const searchField = filterConfig.fields.find((f) => f.type === 'search');
  const searchValue = searchField?.name && filters[searchField.name]
    ? String(filters[searchField.name])
    : '';
  const debouncedSearch = useDebounce(searchValue, 500);

  // Get endpoint URL
  const endpoint = useMemo(() => {
    const ep = configEndpoints?.list || entity?.apiEndpoint?.list;
    if (!ep) {
      console.error('GenericListView: Missing configEndpoints.list');
    }
    return ep;
  }, [configEndpoints, entity]);

  const listMethod = useMemo(
    () => configEndpoints?.listMethod || entity?.apiEndpoint?.listMethod || 'POST',
    [configEndpoints, entity]
  );


  // View settings (table/card, visible columns/fields)
  const viewSettings = useLocalStorage(entity.storageKey, {
    view: 'table',
    visibleColumns: Object.fromEntries(tableConfig.columns.map((col) => [col.id, true])),
    columnOrder: tableConfig.columns,
    // Card view settings (optional, check if card config exists)
    visibleCardFields: card?.metadata ? Object.fromEntries(card.metadata.map((field) => [field.id, true])) : {},
    cardFieldOrder: card?.metadata || [],
  });

  // After the viewSettings declaration (around line 100-103)
  const view = useMemo(() => viewSettings.state.view || 'table', [viewSettings.state.view]);

  // Table Visibility
  const visibleColumns = useMemo(() => {
    const cached = viewSettings.state.visibleColumns || {};
    const merged = { ...cached };

    // Ensure new columns are visible by default if they aren't in cached settings
    tableConfig.columns.forEach(col => {
      if (merged[col.id] === undefined) {
        merged[col.id] = true;
      }
    });
    return merged;
  }, [viewSettings.state.visibleColumns, tableConfig.columns]);

  const columnOrder = useMemo(() => {
    const cached = viewSettings.state.columnOrder;
    if (!cached || !Array.isArray(cached) || cached.length === 0) return tableConfig.columns;

    const cachedIds = new Set(cached.map(c => c.id));

    // Keep cached order for existing columns
    const validCached = cached.filter(c => tableConfig.columns.some(tc => tc.id === c.id));

    // Append any newly added columns from config
    const newCols = tableConfig.columns.filter(tc => !cachedIds.has(tc.id));

    return [...validCached, ...newCols];
  }, [viewSettings.state.columnOrder, tableConfig.columns]);

  // Card Visibility
  const visibleCardFields = useMemo(
    () => viewSettings.state.visibleCardFields || {},
    [viewSettings.state.visibleCardFields]
  );
  const cardFieldOrder = useMemo(
    () => viewSettings.state.cardFieldOrder || card?.metadata || [],
    [viewSettings.state.cardFieldOrder, card?.metadata]
  );

  // Build POST request body (NOW safe to use columnOrder and visibleColumns)
  const currentUserId = user?.orgUserId || user?._id || user?.id;

  const requestBody = useMemo(() => {
    // Get selected fields from visible columns
    const selectedFields = processedFeatures.columnVisibility
      ? [...new Set([
        ...columnOrder
          .filter((col) => visibleColumns[col.id] === true)
          .map((col) => col.id),
        ...(fetchConfig?.listFields || []),
        'createdBy',
        'updatedBy',
        'userId',
        'expense_by',
        'reportingManagerId',
        'reportingManager'
      ])]
      : []; // Empty array = fetch all fields

    const body = {
      page: table.page + 1,
      limit: table.rowsPerPage,
      sortField,
      sortDirection,
      selectedFields, // Field selection for backend
      includePopulations: true, // Enable population of related docs
    };

    // Add filter parameters to body
    filterConfig.fields.forEach((field) => {
      let value = filters[field.name];

      if (field.type === 'search') {
        value = debouncedSearch;
      }

      // For autocomplete, extract the value from the object
      if (field.type === 'autocomplete' && value !== null) {
        if (Array.isArray(value)) {
          value = value.map((v) => (typeof v === 'object' && v !== null ? (v.value || v._id || v.id || v) : v));
        } else if (typeof value === 'object') {
          value = value.value || value._id || value.id;
        }
      }

      if (field.type === 'dateRange') {
        if (value && (value.start || value.end)) {
          body[field.apiParam] = {};
          if (value.start) body[field.apiParam].start = value.start.toISOString();
          if (value.end) body[field.apiParam].end = value.end.toISOString();
        }
      } else if (field.type === 'date' && value instanceof Date) {
        body[field.apiParam] = value.toISOString();
      } else if (value !== undefined && value !== null && value !== 'all' && value !== '') {
        body[field.apiParam] = value;
      }
    });

    // --- SELF RIGHTS LOGIC ---
    // If user has view_self but NOT view_all, force createdBy filter
    if (processedFeatures.viewSelf && !processedFeatures.viewAll) {
      body.createdBy = user?._id || user?.id;
    }

    // --- REPORTEE TYPE LOGIC ---
    const showReporteeTabs = processedFeatures.reporteeFiltering && (
      processedFeatures.isAdmin || 
      (rightsState && typeof rightsState.hasRight === 'function' && (rightsState.hasRight('approve') || rightsState.hasRight('approve_all') || rightsState.hasRight('view_all')))
    );
    if (showReporteeTabs) {
      body.reporteeType = reporteeType;
    }

    return body;
  }, [
    table.page,
    table.rowsPerPage,
    debouncedSearch,
    filters,
    filterConfig,
    sortField,
    sortDirection,
    columnOrder,
    visibleColumns,
    processedFeatures.columnVisibility,
    processedFeatures.viewAll,
    processedFeatures.viewSelf,
    user?._id,
    user?.id,
    reporteeType,
    processedFeatures.isAdmin,
    processedFeatures.reporteeFiltering,
    rightsState,
    fetchConfig?.listFields,
  ]);


  // Reset page when search changes
  useEffect(() => {
    if (debouncedSearch !== filters[searchField?.name]) return;
    if (filters[searchField?.name]) table.onResetPage();
  }, [debouncedSearch, filters, searchField, table]);

  // Debug Endpoint and Body
  useEffect(() => {
    console.log('📊 GenericListView Debug:', { endpoint, requestBody });
  }, [endpoint, requestBody]);

  const swrKey = useMemo(() => {
    if (!endpoint) return null;
    if (listMethod === 'GET') {
      return [endpoint, { params: requestBody }];
    }
    return [endpoint, requestBody];
  }, [endpoint, listMethod, requestBody]);

  // Fetch data dynamically based on the configured listMethod
  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    listMethod === 'GET' ? fetcher : postFetcher,
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
    }
  );

  const limitReached = useMemo(() => {
    if (entity?.name !== 'User') return false;
    const userLimit = data?.userLimit;
    const activeUserCount = data?.activeUserCount;
    if (typeof userLimit !== 'number' || userLimit === null) return false;
    return activeUserCount >= userLimit;
  }, [entity?.name, data?.userLimit, data?.activeUserCount]);

  // Re-fetch when refreshKey changes
  useEffect(() => {
    if (refreshKey) {
      mutate();
    }
  }, [refreshKey, mutate]);

  // Transform data
  const tableData = useMemo(() => {
    if (!data?.data) return [];
    const mapped = data.data.map((item) => ({
      id: item.id || item._id,
      ...item,
    }));

    if (config.transformTableData) {
      return config.transformTableData(mapped);
    }
    return mapped;
  }, [data, config]);

  // Handlers
  const handleFilters = useCallback(
    (name, value) => {
      table.onResetPage();
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [table]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(filterConfig.default);
  }, [filterConfig]);

  const handleCreate = useCallback(
    async (formData) => {
      // Transform data for submission
      let payload;
      if (features.transformDataForSubmit) {
        payload = features.transformDataForSubmit(formData);
      } else if (config.transformPayload) {
        payload = config.transformPayload(formData);
      } else {
        payload = formData;
      }

      const entityName = typeof entity === 'string' ? entity : entity.name;

      // Detect if payload is FormData - let browser set Content-Type automatically
      const isFormData = payload instanceof FormData;
      const axiosConfig = { headers: {} };

      if (isFormData) {
        axiosConfig.headers['Content-Type'] = undefined;
      }

      // Attach custom API headers if provided (e.g., S3 Slugs)
      if (entity?.apiEndpoint?.apiHeaders) {
        Object.assign(axiosConfig.headers, entity.apiEndpoint.apiHeaders);
      }

      if (currentItem && !currentItem._isDuplicate) {
        const itemId = currentItem.id || currentItem._id;
        const updateUrl = configEndpoints?.update ? configEndpoints.update(itemId) : entity?.apiEndpoint?.update(itemId);
        const response = await axiosInstance.put(updateUrl, payload, axiosConfig);
        enqueueSnackbar(`${entityName} updated successfully!`, { variant: 'success' });

        // Call onUpdateSuccess callback if defined
        if (config.onUpdateSuccess) {
          const updatedData = response.data?.data || response.data;
          await config.onUpdateSuccess(updatedData, { user, refreshUser });
        }
      } else {
        const createUrl = configEndpoints?.create || entity?.apiEndpoint?.create;
        const response = await axiosInstance.post(createUrl, payload, axiosConfig);
        enqueueSnackbar(`${entityName} ${currentItem?._isDuplicate ? 'duplicated' : 'created'} successfully!`, { variant: 'success' });

        // Call onCreateSuccess callback if defined
        if (config.onCreateSuccess) {
          const createdData = response.data?.data || response.data;
          await config.onCreateSuccess(createdData, { user, refreshUser });
        }
      }
      mutate();
      setCurrentItem(null);
    },
    [currentItem, mutate, enqueueSnackbar, entity, config, configEndpoints, user, refreshUser, features]
  );

  const handleDuplicate = useCallback(
    async (id) => {
      try {
        // Show loading state
        setCurrentItem({ _loading: true });
        quickCreate.onTrue();

        // Fetch full data
        let getByIdUrl;
        if (configEndpoints?.getById) {
          getByIdUrl = configEndpoints.getById(id);
        } else if (configEndpoints?.details) {
          getByIdUrl = configEndpoints.details(id);
        } else if (entity?.apiEndpoint?.getById) {
          getByIdUrl = entity.apiEndpoint.getById(id);
        } else if (entity?.apiEndpoint?.details) {
          getByIdUrl = entity.apiEndpoint.details(id);
        } else {
          getByIdUrl = `${configEndpoints?.list || entity?.apiEndpoint?.list}/${id}`;
        }

        const response = await axiosInstance.get(getByIdUrl);
        const recordData = response.data?.data || response.data;

        // Clean up unique/ID fields for duplication
        const duplicateData = { ...recordData };
        delete duplicateData._id;
        delete duplicateData.id;
        delete duplicateData.employeeId;
        delete duplicateData.email;
        delete duplicateData.phone;
        delete duplicateData.username;
        delete duplicateData.createdAt;
        delete duplicateData.updatedAt;

        // Mark as duplicate (this skips the PUT path in handleCreate)
        setCurrentItem({ ...duplicateData, _isDuplicate: true });
      } catch (err) {
        console.error('Failed to duplicate:', err);
        enqueueSnackbar('Failed to load record for duplication', { variant: 'error' });
        quickCreate.onFalse();
        setCurrentItem(null);
      }
    },
    [configEndpoints, entity, quickCreate, enqueueSnackbar]
  );

  const handleAddNew = useCallback(async () => {
    setCurrentItem(null);

    // Call custom config prefill logic if provided
    let initialPrefill = null;
    if (config.getCreatePrefill && tableData) {
      initialPrefill = config.getCreatePrefill(tableData);
    }
    setPrefillData(initialPrefill);

    quickCreate.onTrue();

    const anyPrefill = form.fields.some(f => f.allowPrefill || (f.fields && f.fields.some(nf => nf.allowPrefill)));

    // Check for prefill ONLY if formId is provided and we have fields marked for prefill
    if (entity.formId && anyPrefill && user?.id) {
      try {
        setPrefillLoading(true);
        const res = await axiosInstance.post(`${endpoints.formData.root}/list`, {
          formId: entity.formId,
          limit: 1,
          sortField: 'createdAt',
          sortDirection: 'desc',
          submittedBy: user.id
        });

        const lastRecord = res.data.data?.[0];
        if (lastRecord) {
          const prefill = {};
          const collectPrefill = (fs) => {
            fs.forEach(f => {
              if (f.allowPrefill && lastRecord[f.name] !== undefined) {
                prefill[f.name] = lastRecord[f.name];
              }
              if (f.fields) collectPrefill(f.fields);
            });
          };
          collectPrefill(form.fields);

          if (Object.keys(prefill).length > 0) {
            setPrefillData(prefill);
          }
        }
      } catch (err) {
        console.error('Prefill fetch failed:', err);
      } finally {
        setPrefillLoading(false);
      }
    }
  }, [entity.formId, form.fields, user?.id, quickCreate, config, tableData]);

  const handleEdit = useCallback(
    async (id) => {
      try {
        // Show loading state immediately
        setCurrentItem({ _loading: true });
        quickCreate.onTrue();

        // Fetch ONLY this ONE record with full data
        let getByIdUrl;
        if (configEndpoints?.getById) {
          getByIdUrl = configEndpoints.getById(id);
        } else if (configEndpoints?.details) {
          getByIdUrl = configEndpoints.details(id);
        } else if (entity?.apiEndpoint?.getById) {
          getByIdUrl = entity.apiEndpoint.getById(id);
        } else if (entity?.apiEndpoint?.details) {
          getByIdUrl = entity.apiEndpoint.details(id);
        } else {
          getByIdUrl = `${configEndpoints?.list || entity?.apiEndpoint?.list}/${id}`;
        }

        const response = await axiosInstance.get(getByIdUrl);

        // Extract data from response (handle both {data: record} and {data: {data: record}})
        const recordData = response.data?.data || response.data;

        setCurrentItem(recordData);
      } catch (err) {
        console.error('Failed to fetch record for edit:', err);
        enqueueSnackbar(
          err.response?.data?.message || 'Failed to load record',
          { variant: 'error' }
        );
        quickCreate.onFalse();
        setCurrentItem(null);
      }
    },
    [configEndpoints, entity, quickCreate, enqueueSnackbar]
  );

  const handleDelete = useCallback(
    async (id) => {
      try {
        const deleteUrl = configEndpoints?.delete ? configEndpoints.delete(id) : entity?.apiEndpoint?.delete(id);
        await axiosInstance.delete(deleteUrl);
        mutate();
        const entityName = typeof entity === 'string' ? entity : entity.name;
        enqueueSnackbar(`${entityName} deleted successfully!`, { variant: 'success' });
      } catch (err) {
        const entityName = typeof entity === 'string' ? entity : entity.name;
        enqueueSnackbar(`Failed to delete ${entityName}`, { variant: 'error' });
      }
    },
    [mutate, enqueueSnackbar, entity, configEndpoints]
  );

  const handleBulkDelete = useCallback(
    async (ids) => {
      try {
        if (entity?.formId) {
          // Optimized single-request bulk delete for dynamic forms
          await axiosInstance.post('/api/admin/form-data/bulk-delete', {
            ids,
            formId: entity.formId
          });
        } else {
          // Fallback loop-delete for standard entities
          const getDeleteUrl = (id) => (configEndpoints?.delete ? configEndpoints.delete(id) : entity?.apiEndpoint?.delete(id));
          await Promise.all(ids.map((id) => axiosInstance.delete(getDeleteUrl(id))));
        }

        mutate();
        const entityNamePlural = typeof entity === 'string' ? `${entity}s` : entity.namePlural;
        enqueueSnackbar(`${ids.length} ${entityNamePlural} deleted successfully!`, {
          variant: 'success',
        });
      } catch (err) {
        const entityNamePlural = typeof entity === 'string' ? `${entity}s` : entity.namePlural;
        enqueueSnackbar(`Failed to delete ${entityNamePlural}`, { variant: 'error' });
      }
    },
    [mutate, enqueueSnackbar, entity, configEndpoints]
  );

  const handleDeleteAll = useCallback(
    async () => {
      try {
        if (entity?.formId) {
          // Dynamic form: bulk delete via dedicated endpoint
          const response = await axiosInstance.post('/api/admin/form-data/delete-all', {
            formId: entity.formId,
            projectId: filters?.projectId
          });

          mutate();
          enqueueSnackbar(response.data?.message || 'All records deleted successfully!', { variant: 'success' });
        } else {
          // Standard entity: fetch all IDs then delete each
          const listEndpoint = configEndpoints?.list || entity?.apiEndpoint?.list;
          if (!listEndpoint) return;

          let fetchResponse;
          try {
            fetchResponse = await axiosInstance.post(listEndpoint, { page: 1, limit: 0, selectedFields: ['_id'] });
          } catch {
            fetchResponse = await axiosInstance.get(listEndpoint, { params: { page: 1, limit: 0 } });
          }

          const allItems = fetchResponse.data?.data || [];
          const getDeleteUrl = (id) => (configEndpoints?.delete ? configEndpoints.delete(id) : entity?.apiEndpoint?.delete(id));
          await Promise.all(allItems.map((item) => axiosInstance.delete(getDeleteUrl(item._id || item.id))));

          mutate();
          const entityNamePlural = typeof entity === 'string' ? `${entity}s` : entity.namePlural;
          enqueueSnackbar(`All ${entityNamePlural} deleted successfully!`, { variant: 'success' });
        }
      } catch (err) {
        console.error('Delete all failed:', err);
        const message = err.response?.data?.message || err.message || '';

        if (message.includes('compulsory')) {
          enqueueSnackbar('Compulsory: Please export data before deleting all records.', {
            variant: 'warning',
            autoHideDuration: 6000
          });
        } else {
          enqueueSnackbar('Failed to delete all records', { variant: 'error' });
        }
      }
    },
    [mutate, enqueueSnackbar, entity, configEndpoints, filters?.projectId]
  );

  const handleViewChange = useCallback(
    (event, newView) => {
      if (newView !== null) {
        viewSettings.update('view', newView);
      }
    },
    [viewSettings]
  );

  const handleToggleColumn = useCallback(
    (columnId, visible) => {
      viewSettings.update('visibleColumns', {
        ...visibleColumns,
        [columnId]: visible,
      });
    },
    [viewSettings, visibleColumns]
  );

  const handleReorderColumns = useCallback(
    (newOrder) => {
      viewSettings.update('columnOrder', newOrder);
    },
    [viewSettings]
  );

  const handleResetColumns = useCallback(() => {
    viewSettings.update(
      'visibleColumns',
      Object.fromEntries(tableConfig.columns.map((col) => [col.id, true]))
    );
    viewSettings.update('columnOrder', tableConfig.columns);
  }, [viewSettings, tableConfig.columns]);

  const handleToggleCardField = useCallback(
    (fieldId, visible) => {
      viewSettings.update('visibleCardFields', {
        ...visibleCardFields,
        [fieldId]: visible,
      });
    },
    [viewSettings, visibleCardFields]
  );

  const handleReorderCardFields = useCallback(
    (newOrder) => {
      viewSettings.update('cardFieldOrder', newOrder);
    },
    [viewSettings]
  );

  const handleResetCardFields = useCallback(() => {
    if (card?.metadata) {
      viewSettings.update(
        'visibleCardFields',
        Object.fromEntries(card.metadata.map((field) => [field.id, true]))
      );
      viewSettings.update('cardFieldOrder', card.metadata);
    }
  }, [viewSettings, card?.metadata]);

  const handleFilterStatus = useCallback(
    (event, newValue) => {
      if (newValue !== null && newValue !== undefined) {
        const statusField = filterConfig.fields.find((f) => f.type === 'tabs');
        if (statusField) {
          handleFilters(statusField.name, newValue);
        }
      }
    },
    [handleFilters, filterConfig]
  );

  const canReset = !isEqual(filterConfig.default, filters);
  const statusField = filterConfig.fields.find((f) => f.type === 'tabs');

  if (error) {
    return (
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <Card sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">Failed to load {entity.namePlural}</Typography>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      {preTableContent && (
        <Box sx={{ mb: 3 }}>
          {preTableContent}
        </Box>
      )}

      {/* ✅ Breadcrumbs with Actions */}
      <CustomBreadcrumbs
        heading={
          entity?.name === 'User' && typeof data?.userLimit === 'number'
            ? `${breadcrumbs?.heading || entity.namePlural} (${data.activeUserCount}/${data.userLimit})`
            : (breadcrumbs?.heading || entity.namePlural)
        }
        links={breadcrumbs?.links || []}
        action={
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* Column Visibility - Show in table view */}
            {view === 'table' && features.columnVisibility && (
              <TableColumnVisibility
                columns={columnOrder}
                visibleColumns={visibleColumns}
                onToggleColumn={handleToggleColumn}
                onReorder={handleReorderColumns}
                onReset={handleResetColumns}
              />
            )}

            {/* Field Visibility - Show in card view */}
            {view === 'card' && features.columnVisibility && card?.metadata && (
              <TableColumnVisibility
                columns={cardFieldOrder}
                visibleColumns={visibleCardFields}
                onToggleColumn={handleToggleCardField}
                onReorder={handleReorderCardFields}
                onReset={handleResetCardFields}
              />
            )}

            {/* View Toggle */}
            {features.viewToggle && (
              <ToggleButtonGroup
                size="small"
                value={view}
                exclusive
                onChange={handleViewChange}
                sx={{
                  border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.12)}`,
                }}
              >
                <ToggleButton value="table" sx={{ px: 1.5 }}>
                  <Iconify icon="mingcute:list-check-3-line" width={20} />
                </ToggleButton>
                <ToggleButton value="card" sx={{ px: 1.5 }}>
                  <Iconify icon="mingcute:grid-line" width={20} />
                </ToggleButton>
              </ToggleButtonGroup>
            )}

            {processedFeatures.create !== false && !limitReached && (
              <Button
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
                onClick={handleAddNew}
              >
                New {entity.name}
              </Button>
            )}

            {/* Extra Actions (Injected) */}
            {extraActions}
          </Stack>
        }
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      />

      {limitReached && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have reached the maximum active user limit of {data?.userLimit} users. To add or duplicate users, please deactivate existing active users or contact support to increase your limit.
        </Alert>
      )}

      <Card>
        {/* Reportee Filter Tabs (Admin or users with view_all/approve rights) */}
        {(processedFeatures.isAdmin || (rightsState && typeof rightsState.hasRight === 'function' && (rightsState.hasRight('approve') || rightsState.hasRight('approve_all') || rightsState.hasRight('view_all')))) && processedFeatures.reporteeFiltering && (
          <Tabs
            value={reporteeType}
            onChange={(_, newValue) => {
              setReporteeType(newValue);
              table.onResetPage();
            }}
            sx={{
              px: 2.5,
              borderBottom: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            <Tab 
              value="all" 
              label="All Reportees" 
              icon={<Iconify icon="solar:users-group-two-rounded-bold-duotone" width={20} />} 
              iconPosition="start" 
            />
            <Tab 
              value="my" 
              label="My Reportees" 
              icon={<Iconify icon="solar:user-hand-up-bold-duotone" width={20} />} 
              iconPosition="start" 
            />
          </Tabs>
        )}

        {/* Status Tabs */}
        {statusField && (
          <Tabs
            value={filters[statusField.name]}
            onChange={handleFilterStatus}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {statusField.options.map((tab) => (
              <Tab
                key={tab.value}
                iconPosition="end"
                value={tab.value}
                label={tab.label}
                icon={
                  statusField.showCount && (
                    <Label
                      variant={
                        ((tab.value === 'all' || filters[statusField.name] === tab.value) &&
                          'filled') ||
                        'soft'
                      }
                      color={
                        tab.color ||
                        (tab.value === true && 'success') ||
                        (tab.value === false && 'error') ||
                        'default'
                      }
                    >
                      {data?.statusCounts?.[tab.value] || 0}
                    </Label>
                  )
                }
              />
            ))}
          </Tabs>
        )}

        {/* Toolbar & Filters */}
        <GenericToolbar
          config={filterConfig}
          filters={filters}
          onFilters={handleFilters}
          onAdvancedFilters={advancedFilters.onTrue}
          onImport={importDialog.onTrue}
          onExport={exportDialog.onTrue}
          features={processedFeatures}
        />

        {canReset && (
          <GenericFiltersResult
            config={filterConfig}
            filters={filters}
            onFilters={handleFilters}
            onResetFilters={handleResetFilters}
            results={data?.pagination?.total ?? data?.totalCount ?? 0}
          />
        )}

        {/* Table or Card View */}
        {view === 'table' ? (
          <GenericTable
            config={{
              ...tableConfig,
              onBulkApprove: config.onBulkApprove,
              onBulkReject: config.onBulkReject,
              onCustomAction: config.onCustomAction,
              togglingRows,
              // Wrap onToggleApprove to provide helper functions
              onToggleApprove: tableConfig.onToggleApprove ? async (row, checked) => {
                await tableConfig.onToggleApprove(row, checked, {
                  showSnackbar: enqueueSnackbar,
                  refreshData: mutate,
                  setTogglingRows,
                });
              } : undefined,
            }}
            data={tableData}
            totalCount={data?.pagination?.total ?? data?.totalCount ?? 0}
            table={table}
            onEdit={handleEdit}
            onDuplicate={onDuplicate || handleDuplicate}
            onDelete={handleDelete}
            onBulkDelete={handleBulkDelete}
            onDeleteAll={handleDeleteAll}
            currentUserId={currentUserId}
            currentUser={user}
            onBulkAction={onBulkAction}
            form={form}
            entityConfig={config}
            bulkActionLabel={bulkActionLabel}
            visibleColumns={visibleColumns}
            columnOrder={columnOrder}
            features={processedFeatures}
            isLoading={isLoading}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            limitReached={limitReached}
          />
        ) : (
          <Box sx={{ p: 3 }}>
            <GenericCardView
              config={card}
              data={tableData}
              onEdit={handleEdit}
              onDuplicate={onDuplicate || handleDuplicate}
              onDelete={handleDelete}
              features={processedFeatures}
              tableConfig={{
                ...tableConfig,
                onBulkApprove: config.onBulkApprove,
                onBulkReject: config.onBulkReject,
                onCustomAction: config.onCustomAction,
                togglingRows,
                onToggleApprove: tableConfig.onToggleApprove ? async (row, checked) => {
                  await tableConfig.onToggleApprove(row, checked, {
                    showSnackbar: enqueueSnackbar,
                    refreshData: mutate,
                    setTogglingRows,
                  });
                } : undefined,
              }}
              table={table}
              customActions={card?.customActions}
              visibleFields={visibleCardFields}
              currentUserId={currentUserId}
              fullConfig={config}
              limitReached={limitReached}
            />
            <TablePaginationCustom
              count={data?.pagination?.total ?? data?.totalCount ?? 0}
              page={table.page}
              rowsPerPage={table.rowsPerPage}
              onPageChange={table.onChangePage}
              onRowsPerPageChange={table.onChangeRowsPerPage}
              dense={table.dense}
              onChangeDense={table.onChangeDense}
            />
          </Box>
        )}
      </Card>

      {/* Create/Edit Dialog */}
      <QuickFormDialog
        open={quickCreate.value}
        onClose={() => {
          quickCreate.onFalse();
          setCurrentItem(null);
        }}
        onSubmit={handleCreate}
        title={(() => {
          if (currentItem?._loading) return 'Loading...';
          return currentItem ? form.title.edit : form.title.create;
        })()}
        submitButtonText={(() => {
          if (currentItem?._isDuplicate) return form.submitButtonText?.duplicate || 'Create Duplicate';
          return currentItem ? (form.submitButtonText?.edit || 'Update') : (form.submitButtonText?.create || 'Create');
        })()}
        fields={form.fields}
        currentData={(() => {
          if (currentItem) {
            const normalizer = config.normalizeDataForForm || processedFeatures.normalizeDataForForm;
            const normalized = normalizer ? normalizer(currentItem) : currentItem;
            return {
              _id: currentItem._id || currentItem.id,
              id: currentItem.id || currentItem._id,
              _isDuplicate: currentItem._isDuplicate,
              ...normalized,
            };
          }
          return prefillData;
        })()}
        maxWidth={form.maxWidth}
        showAvatar={form.showAvatar}
        isLoading={currentItem?._loading || prefillLoading}  // Pass loading state
        errorMessage={`Failed to save ${typeof entity === 'string' ? entity : entity.name}`}
        formType={config.formType}
        primaryFormId={config.primaryFormId}
        isVerificationRequired={config.isVerificationRequired}
        autoFillLookupFieldId={config.autoFillLookupFieldId}
        projectId={config.projectId}
        isPublic={config.isPublic}
        isCustomForm={config.isCustomForm}
      />

      {/* Advanced Filters Modal */}
      <AdvancedFiltersModal
        open={advancedFilters.value}
        onClose={advancedFilters.onFalse}
        config={filterConfig}
        filters={filters}
        onFilters={setFilters}
      />

      {renderImportDialog()}

      {/* Export Dialog */}
      {processedFeatures.export && (
        <ExportDialog
          open={exportDialog.value}
          onClose={exportDialog.onFalse}
          config={config}
          filters={filters}
          visibleColumns={Object.keys(visibleColumns || {}).filter(k => visibleColumns[k])}
        />
      )}
    </Container>
  );
}

GenericListView.propTypes = {
  config: PropTypes.shape({
    entity: PropTypes.shape({
      name: PropTypes.string.isRequired,
      namePlural: PropTypes.string.isRequired,
      apiEndpoint: PropTypes.shape({
        list: PropTypes.string.isRequired,
        listMethod: PropTypes.string,
        create: PropTypes.string.isRequired,
        update: PropTypes.func.isRequired,
        delete: PropTypes.func.isRequired,
        getById: PropTypes.func, // Added for on-demand fetching via entity
        details: PropTypes.func,
        apiHeaders: PropTypes.object,
      }).isRequired,
      formId: PropTypes.string,
      storageKey: PropTypes.string.isRequired,
      rights: PropTypes.shape({
        module: PropTypes.string,
        menu: PropTypes.string,
        submenu: PropTypes.string,
      }),
    }).isRequired,
    form: PropTypes.shape({
      title: PropTypes.shape({
        create: PropTypes.string.isRequired,
        edit: PropTypes.string.isRequired,
      }).isRequired,
      submitButtonText: PropTypes.shape({
        create: PropTypes.string,
        edit: PropTypes.string,
        duplicate: PropTypes.string,
      }),
      fields: PropTypes.array.isRequired,
      showAvatar: PropTypes.bool,
      maxWidth: PropTypes.string,
    }).isRequired,
    table: PropTypes.shape({
      columns: PropTypes.array.isRequired,
      defaultRowsPerPage: PropTypes.number,
      defaultSort: PropTypes.shape({
        field: PropTypes.string,
        direction: PropTypes.oneOf(['asc', 'desc']),
      }),
      onToggleApprove: PropTypes.func,
      togglingRows: PropTypes.instanceOf(Set),
    }).isRequired,
    endpoints: PropTypes.shape({
      list: PropTypes.string,
      listMethod: PropTypes.string,
      create: PropTypes.string,
      update: PropTypes.func,
      delete: PropTypes.func,
      getById: PropTypes.func,  // Added for on-demand fetching
      details: PropTypes.func,
      formData: PropTypes.shape({
        root: PropTypes.string,
      }),
    }),
    filters: PropTypes.shape({
      default: PropTypes.object.isRequired,
      fields: PropTypes.array.isRequired,
    }).isRequired,
    card: PropTypes.shape({
      title: PropTypes.func.isRequired,
      subtitle: PropTypes.func,
      avatar: PropTypes.func,
      status: PropTypes.func,
      metadata: PropTypes.array,
      customActions: PropTypes.func,
    }),
    fetch: PropTypes.shape({
      listFields: PropTypes.arrayOf(PropTypes.string),
    }),
    features: PropTypes.shape({
      create: PropTypes.bool,
      edit: PropTypes.bool,
      delete: PropTypes.bool,
      bulkDelete: PropTypes.bool,
      export: PropTypes.bool,
      import: PropTypes.bool,
      viewToggle: PropTypes.bool,
      columnVisibility: PropTypes.bool,
      viewAll: PropTypes.bool,
      viewSelf: PropTypes.bool,
      editAll: PropTypes.bool,
      editSelf: PropTypes.bool,
      deleteAll: PropTypes.bool,
      deleteSelf: PropTypes.bool,
      isAdmin: PropTypes.bool,
      duplicate: PropTypes.bool,
      bulkApprove: PropTypes.bool,
      bulkReject: PropTypes.bool,
      customActions: PropTypes.array,
      normalizeDataForForm: PropTypes.func,
      transformDataForSubmit: PropTypes.func,
    }),
    transformPayload: PropTypes.func,
    normalizeDataForForm: PropTypes.func,
    onFiltersChange: PropTypes.func,
    onBulkApprove: PropTypes.func,
    onBulkReject: PropTypes.func,

    onCustomAction: PropTypes.func,
    transformTableData: PropTypes.func,
    onUpdateSuccess: PropTypes.func,
    onCreateSuccess: PropTypes.func,
    getCreatePrefill: PropTypes.func,
    _formMaster: PropTypes.object, // Special field for dynamic forms Excel Wizard
    formType: PropTypes.string,
    primaryFormId: PropTypes.string,
    isVerificationRequired: PropTypes.bool,
    autoFillLookupFieldId: PropTypes.string,
    projectId: PropTypes.string,
    isPublic: PropTypes.bool,
    isCustomForm: PropTypes.bool,
  }).isRequired,
  breadcrumbs: PropTypes.shape({
    heading: PropTypes.string,
    links: PropTypes.array,
  }),
  onBulkAction: PropTypes.func,
  bulkActionLabel: PropTypes.string,
  refreshKey: PropTypes.number,
  extraActions: PropTypes.node, // Custom actions in header
  onDuplicate: PropTypes.func,
  ImportDialogComponent: PropTypes.elementType,
  preTableContent: PropTypes.node,
};
